package org.teddapp.project.resources

/**
 * Les règles des ressources d'une compétence, celles du contrat avec le serveur
 * (contrat-api-ressources.md, sections 2 et 3), vérifiées sur l'appareil avant l'envoi :
 * le serveur refuserait sinon la ressource, et l'utilisateur ne saurait pas pourquoi.
 */
object ResourceRules {
    const val MAX_PER_SKILL = 300
    /** Au-delà, le compte local peut être en retard : on demande le chiffre au serveur. */
    const val CHECK_SERVER_ABOVE = 280
    const val TITLE_MAX = 200
    const val TITLE_PREFILL = 80
    const val URL_MAX = 2_048
    const val BODY_MAX = 100_000
    /** Délai avant que la place d'un fichier supprimé soit rendue au quota. */
    const val PURGE_DAYS = 90
    /**
     * Ce qu'un sélecteur lit au plus d'un fichier : un octet de plus que le plus gros
     * format permis. Un fichier plus gros est refusé sans être chargé en entier.
     */
    const val READ_LIMIT = 20 * 1024 * 1024 + 1

    private const val MO = 1024L * 1024L

    /** Ce que l'on accepte d'un fichier, par format. */
    class FileKind(val format: String, val label: String, val extensions: List<String>, val mimes: List<String>, val maxBytes: Long)

    val FILE_KINDS = listOf(
        FileKind("pdf", "PDF", listOf("pdf"), listOf("application/pdf"), 20 * MO),
        FileKind(
            "word", "Word", listOf("docx"),
            listOf("application/vnd.openxmlformats-officedocument.wordprocessingml.document"), 20 * MO,
        ),
        FileKind("image", "Image", listOf("png", "jpg", "jpeg", "webp"), listOf("image/png", "image/jpeg", "image/webp"), 10 * MO),
        FileKind("csv", "CSV", listOf("csv"), listOf("text/csv"), 5 * MO),
        FileKind("markdown", "Markdown", listOf("md", "markdown"), listOf("text/markdown"), 2 * MO),
        FileKind("text", "Texte", listOf("txt"), listOf("text/plain"), 2 * MO),
        FileKind("html", "Page web", listOf("html", "htm"), listOf("text/html"), 5 * MO),
    )

    /** Les extensions proposées par le sélecteur de fichiers. */
    val FILE_EXTENSIONS: List<String> = FILE_KINDS.flatMap { it.extensions }

    /** Le nom affiché d'un format. */
    fun formatLabel(origin: String, format: String): String = when {
        origin == "link" && format == "pdf" -> "Lien · PDF"
        origin == "link" -> "Lien"
        origin == "text" && format == "markdown" -> "Texte · Markdown"
        origin == "text" -> "Texte"
        else -> FILE_KINDS.firstOrNull { it.format == format }?.label ?: format
    }

    fun extensionOf(name: String): String = name.substringAfterLast('.', "").lowercase()

    /** Le format et le type MIME d'un fichier d'après son nom, ou null s'il n'est pas accepté. */
    fun kindOf(fileName: String): FileKind? = extensionOf(fileName).let { ext -> FILE_KINDS.firstOrNull { ext in it.extensions } }

    /** Le type MIME envoyé au serveur : celui du format, d'après l'extension, jamais celui de la plateforme. */
    fun mimeOf(kind: FileKind, fileName: String): String = when (kind.format) {
        "image" -> when (extensionOf(fileName)) {
            "png" -> "image/png"
            "webp" -> "image/webp"
            else -> "image/jpeg"
        }
        else -> kind.mimes.first()
    }

    // ── Titres pré-remplis, modifiables ─────────────────────────────────────

    /** Un lien : le nom du site, sans « www. ». */
    fun titleForLink(url: String): String {
        val rest = url.trim().substringAfter("://", "")
        val host = rest.substringBefore('/').substringBefore('?').substringBefore('#').substringAfterLast('@').substringBefore(':')
        return host.lowercase().removePrefix("www.")
    }

    /** Un texte : sa première ligne non vide, sans le « # » d'un titre Markdown, coupée à 80 caractères. */
    fun titleForText(body: String): String {
        val line = body.lineSequence().map { it.trim() }.firstOrNull { it.isNotEmpty() } ?: return ""
        return cut(line.trimStart('#').trim(), TITLE_PREFILL)
    }

    /** Un fichier : son nom sans l'extension. */
    fun titleForFile(fileName: String): String {
        val base = fileName.substringAfterLast('/').substringAfterLast('\\')
        val stem = if (base.contains('.') && !base.startsWith('.')) base.substringBeforeLast('.') else base
        return cut(stem.trim(), TITLE_MAX)
    }

    private fun cut(text: String, max: Int): String {
        if (text.length <= max) return text
        var end = max
        // Ne pas couper une paire de substitution (émoji) en deux.
        if (text[end - 1].isHighSurrogate()) end--
        return text.substring(0, end).trimEnd()
    }

    /** Un texte qui commence comme du Markdown : un titre « # », une liste, un bloc de code. */
    fun looksLikeMarkdown(body: String): Boolean {
        val first = body.lineSequence().map { it.trimStart() }.firstOrNull { it.isNotEmpty() } ?: return false
        return first.startsWith("# ") || first.startsWith("## ") || first.startsWith("```") ||
            first.startsWith("- ") || first.startsWith("* ") || first.startsWith("> ")
    }

    // ── Contrôles avant l'envoi ─────────────────────────────────────────────

    /** Le problème d'un titre, ou null s'il convient. */
    fun titleProblem(title: String): String? = when {
        title.isBlank() -> "Donne un titre à la ressource."
        title.trim().codePointCount() > TITLE_MAX -> "Le titre fait au plus $TITLE_MAX caractères."
        else -> null
    }

    /** Le problème d'un lien, ou null s'il convient. */
    fun urlProblem(url: String): String? = when {
        url.isBlank() -> "Colle l'adresse du lien."
        urlKey(url.trim()) == null -> "L'adresse doit commencer par http:// ou https://, sans espace, et faire au plus $URL_MAX caractères."
        else -> null
    }

    /** Le problème d'un texte, ou null s'il convient. */
    fun bodyProblem(body: String): String? = when {
        body.isBlank() -> "Le texte est vide."
        body.codePointCount() > BODY_MAX -> "Le texte fait au plus 100 000 caractères."
        else -> null
    }

    /** Le problème d'un fichier, ou null s'il convient. */
    fun fileProblem(fileName: String, bytes: ByteArray): String? {
        val kind = kindOf(fileName)
            ?: return "Ce type de fichier n'est pas accepté. Formats possibles : PDF, Word (.docx), image (PNG, JPEG, WebP), CSV, Markdown, texte, page web."
        if (bytes.isEmpty()) return "Le fichier est vide."
        if (bytes.size > kind.maxBytes) return "Un fichier ${kind.label} fait au plus ${kind.maxBytes / MO} Mo."
        if (!looksLike(kind.format, bytes)) return "Le contenu du fichier ne correspond pas à son extension .${extensionOf(fileName)}."
        return null
    }

    /** Un premier regard sur les octets, pour les formats qui ont une signature. */
    private fun looksLike(format: String, bytes: ByteArray): Boolean = when (format) {
        "pdf" -> bytes.size > 4 && bytes.decodeToString(0, 4) == "%PDF"
        "word" -> bytes.size > 3 && bytes[0] == 'P'.code.toByte() && bytes[1] == 'K'.code.toByte()
        "image" -> imageType(bytes) != null
        else -> true
    }

    private fun imageType(bytes: ByteArray): String? = when {
        bytes.size > 3 && bytes[0] == 0xFF.toByte() && bytes[1] == 0xD8.toByte() && bytes[2] == 0xFF.toByte() -> "image/jpeg"
        bytes.size > 4 && bytes[0] == 0x89.toByte() && bytes[1] == 'P'.code.toByte() && bytes[2] == 'N'.code.toByte() && bytes[3] == 'G'.code.toByte() -> "image/png"
        bytes.size > 12 && bytes.decodeToString(0, 4) == "RIFF" && bytes.decodeToString(8, 12) == "WEBP" -> "image/webp"
        else -> null
    }

    private fun String.codePointCount(): Int {
        var n = 0
        var i = 0
        while (i < length) {
            if (this[i].isHighSurrogate() && i + 1 < length && this[i + 1].isLowSurrogate()) i++
            n++
            i++
        }
        return n
    }

    /**
     * La clé de comparaison d'un lien, comme le serveur la calcule : schéma et hôte en
     * minuscules, fragment retiré. Null si l'adresse n'est pas un lien http(s) acceptable.
     */
    fun urlKey(url: String): String? {
        if (url.isEmpty() || url.length > URL_MAX || url.any { it.isWhitespace() }) return null
        val sep = url.indexOf("://").takeIf { it > 0 } ?: return null
        val scheme = url.substring(0, sep).lowercase()
        if (scheme != "http" && scheme != "https") return null
        var rest = url.substring(sep + 3)
        rest = rest.substringBefore('#')
        val end = rest.indexOfFirst { it == '/' || it == '?' }.let { if (it < 0) rest.length else it }
        val host = rest.substring(0, end)
        if (host.isEmpty()) return null
        return "$scheme://${host.lowercase()}${rest.substring(end)}"
    }

    // ── États et messages affichés ──────────────────────────────────────────

    /** L'état d'une ressource tel que l'écran le montre. */
    enum class State { Refused, Uploading, Pending, Indexing, Ready, RefreshFailed, Failed }

    fun stateOf(origin: String, indexStatus: String?, refreshError: String?, pushError: String?, fileUploaded: Boolean?): State = when {
        pushError != null -> State.Refused
        origin == "file" && fileUploaded != true -> State.Uploading
        indexStatus == "ready" && refreshError != null -> State.RefreshFailed
        indexStatus == "ready" -> State.Ready
        indexStatus == "indexing" -> State.Indexing
        indexStatus == "failed" -> State.Failed
        else -> State.Pending
    }

    fun stateLabel(state: State): String = when (state) {
        State.Refused -> "Refusée"
        State.Uploading -> "Envoi en cours"
        State.Pending -> "En attente"
        State.Indexing -> "Analyse en cours"
        State.Ready -> "Prête"
        State.RefreshFailed -> "Actualisation impossible"
        State.Failed -> "Échec"
    }

    /** Le message lisible d'un code `index_error` (section 3.9). */
    fun indexErrorMessage(code: String?): String = when (code) {
        "fetch_failed" -> "Le lien ne répond pas ou renvoie une erreur."
        "fetch_blocked" -> "Ce lien vise une adresse interdite, par exemple une adresse privée."
        "too_large" -> "Le contenu dépasse la taille permise."
        "too_many_pages" -> "Le PDF a trop de pages : 300 au plus, 20 s'il est scanné."
        "too_many_rows" -> "Le fichier CSV a plus de 10 000 lignes."
        "unsupported_content" -> "Le contenu ne correspond pas au format annoncé."
        "empty_content" -> "Aucun texte exploitable n'a été trouvé."
        "extraction_failed" -> "Le fichier est illisible ou abîmé."
        "timeout" -> "L'analyse a pris trop de temps."
        "upload_missing" -> "Le fichier n'a pas été envoyé dans les 7 jours."
        null -> "L'analyse a échoué."
        else -> "L'analyse a échoué ($code)."
    }

    /** Le message d'un refus du serveur (section 3.8). */
    fun refusalMessage(reason: String?): String = when (reason) {
        "duplicate" -> "Cette ressource est déjà dans la compétence : le même lien, ou le même fichier."
        "quota_exceeded" -> "La compétence a déjà $MAX_PER_SKILL ressources. Supprime-en une pour en ajouter."
        "missing_parent" -> "La compétence n'existe plus sur le serveur."
        else -> "Le serveur a refusé cette ressource."
    }

    /** Le message d'un refus de réindexation (section 3.5). */
    fun reindexMessage(code: String): String = when (code) {
        "already_pending" -> "Une analyse est déjà en attente ou en cours."
        "not_uploaded" -> "Le fichier n'est pas encore envoyé : l'analyse partira ensuite."
        "too_soon" -> "Déjà relancée il y a moins d'une heure. Réessaie plus tard."
        "not_found" -> "Le serveur ne connaît pas encore cette ressource : attends la synchronisation."
        else -> "Impossible de relancer l'analyse pour l'instant."
    }

    /** Hexadécimal en minuscules, comme le serveur attend une empreinte SHA-256. */
    fun hex(bytes: ByteArray): String = buildString(bytes.size * 2) {
        for (b in bytes) {
            val v = b.toInt() and 0xff
            append("0123456789abcdef"[v ushr 4])
            append("0123456789abcdef"[v and 0x0f])
        }
    }
}
